import { apiClient } from './api';

export interface SeedImagesResponse {
  restaurants: number;
  categories: number;
  products: number;
  total: number;
}

export type CourierTypeOption = 'PLATFORM' | 'RESTAURANT';
export type DeliveryProviderTypeOption = 'PLATFORM' | 'OWN_FLEET' | 'HYBRID';

export interface CreateCourierDto {
  email: string;
  name: string;
  surname: string;
  phone?: string;
  vehicleType?: 'MOTO' | 'CAR' | 'BIKE';
  plateNumber?: string;
  activeZoneId?: string;
  currentLocation?: { lat: number; lng: number };
  baseDeliveryFee?: number;
  distanceBonusPerKm?: number;
  commissionRate?: number;
  cashBalanceLimit?: number;
  maxCapacity?: number;
  iban?: string;
  courierType?: CourierTypeOption;
  restaurantId?: string;
}

export type BillingCycleOption = 'AUTOMATIC' | 'WEEKLY' | 'MONTHLY' | 'CUSTOM';

export interface CreateRestaurantDto {
  email: string;
  name: string;
  surname: string;
  restaurantName: string;
  slug?: string;
  serviceZoneId: string;
  legalName?: string;
  taxNumber?: string;
  iban?: string;
  commissionRate?: number;
  billingCycle?: BillingCycleOption;
  customBillingCycleDays?: number | null;
  phone?: string;
  phoneSecondary?: string;
  address?: string;
  latitude?: number;
  longitude?: number;
  deliveryProviderType?: DeliveryProviderTypeOption;
  cuisineCategoryIds?: string[];
}

export interface PendingProduct {
  id: string;
  name: string;
  description: string | null;
  price: number;
  imageUrl: string | null;
  category: {
    id: string;
    name: string;
  };
  approvalStatus: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectionReason: string | null;
  createdAt: string;
}

export interface PendingProductsResponse {
  products: PendingProduct[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const adminService = {
  /**
   * Seed demo images from Unsplash
   * Populates all restaurants, categories, and products with placeholder images
   */
  async seedImages(): Promise<SeedImagesResponse> {
    const response = await apiClient.post<SeedImagesResponse>('/admin/seed-images');
    return response.data.data;
  },

  /**
   * Create courier with user profile
   */
  async createCourier(dto: CreateCourierDto) {
    const response = await apiClient.post('/admin/couriers', dto);
    return response.data.data;
  },

  /**
   * Create restaurant with user profile
   */
  async createRestaurant(dto: CreateRestaurantDto) {
    const response = await apiClient.post('/admin/restaurants', dto);
    return response.data.data;
  },

  /**
   * Get pending products for moderation
   */
  async getPendingProducts(page: number = 1, limit: number = 20): Promise<PendingProductsResponse> {
    const response = await apiClient.get<PendingProductsResponse>('/admin/moderation/pending', {
      params: { page, limit },
    });
    return response.data.data;
  },

  /**
   * Approve product
   */
  async approveProduct(productId: string) {
    const response = await apiClient.patch(`/admin/moderation/${productId}/approve`);
    return response.data.data;
  },

  /**
   * Reject product
   */
  async rejectProduct(productId: string, rejectionReason: string) {
    const response = await apiClient.patch(`/admin/moderation/${productId}/reject`, {
      rejectionReason,
    });
    return response.data.data;
  },

  /**
   * Get all couriers
   */
  async getCouriers(page: number = 1, limit: number = 20, search?: string) {
    const response = await apiClient.get('/admin/couriers', {
      params: { page, limit, ...(search ? { search } : {}) },
    });
    return response.data.data;
  },

  /**
   * Get all restaurants
   */
  async getRestaurants(page: number = 1, limit: number = 20, search?: string) {
    const response = await apiClient.get('/admin/restaurants', {
      params: { page, limit, ...(search ? { search } : {}) },
    });
    return response.data.data;
  },

  /**
   * Update courier
   */
  async updateCourier(courierId: string, dto: Partial<CreateCourierDto>) {
    const response = await apiClient.patch(`/admin/couriers/${courierId}`, dto);
    return response.data.data;
  },

  /**
   * Update courier status
   */
  async updateCourierStatus(courierId: string, status: 'ONLINE' | 'OFFLINE' | 'BUSY' | 'ON_DELIVERY' | 'PAUSED') {
    const response = await apiClient.patch(`/admin/couriers/${courierId}/status`, { status });
    return response.data.data;
  },

  /**
   * Update restaurant
   */
  async updateRestaurant(restaurantId: string, dto: Partial<CreateRestaurantDto>) {
    const response = await apiClient.patch(`/admin/restaurants/${restaurantId}`, dto);
    return response.data.data;
  },

  async updateRestaurantStatus(restaurantId: string, isAcceptingOrders: boolean) {
    const response = await apiClient.patch(`/admin/restaurants/${restaurantId}/status`, {
      isAcceptingOrders,
    });
    return response.data.data;
  },

  async updateRestaurantManagerEmail(restaurantId: string, email: string) {
    const response = await apiClient.patch(`/admin/restaurants/${restaurantId}/manager-email`, {
      email,
    });
    return response.data.data;
  },

  async deleteRestaurant(restaurantId: string) {
    const response = await apiClient.delete(`/admin/restaurants/${restaurantId}`);
    return response.data.data;
  },

  async deleteCourier(courierId: string) {
    const response = await apiClient.delete(`/admin/couriers/${courierId}`);
    return response.data.data;
  },

  async getRestaurantCategories(restaurantId: string) {
    const response = await apiClient.get(`/admin/restaurants/${restaurantId}/categories`);
    return response.data.data.categories as AdminRestaurantCategory[];
  },

  async createRestaurantProduct(
    restaurantId: string,
    categoryId: string,
    dto: AdminCreateProductDto,
  ) {
    const response = await apiClient.post(
      `/admin/restaurants/${restaurantId}/categories/${categoryId}/products`,
      dto,
    );
    return response.data.data;
  },

  async getServiceZones(): Promise<{ zones: AdminServiceZone[]; total: number }> {
    const response = await apiClient.get<{ success: boolean; data: { zones: AdminServiceZone[]; total: number } }>(
      '/admin/geo/zones'
    );
    return response.data.data;
  },

  async getServiceZone(id: string): Promise<AdminServiceZoneWithGeoJson | null> {
    const response = await apiClient.get<{
      success: boolean;
      data?: { zone: AdminServiceZoneWithGeoJson };
      error?: { code: string; message: string };
    }>(`/admin/geo/zones/${id}`);
    if (!response.data.success || response.data.error) return null;
    return response.data.data!.zone;
  },

  async createServiceZone(dto: CreateServiceZoneDto): Promise<AdminServiceZone> {
    const response = await apiClient.post<{ success: boolean; data: { zone: AdminServiceZone } }>(
      '/admin/geo/zones',
      dto
    );
    return response.data.data.zone;
  },

  async updateServiceZone(id: string, dto: UpdateServiceZoneDto): Promise<AdminServiceZone> {
    const response = await apiClient.put<{ success: boolean; data: { zone: AdminServiceZone } }>(
      `/admin/geo/zones/${id}`,
      dto
    );
    return response.data.data.zone;
  },

  async deleteServiceZone(id: string): Promise<void> {
    await apiClient.delete(`/admin/geo/zones/${id}`);
  },

  async getSettings(category?: string): Promise<PlatformSetting[]> {
    const params = category ? `?category=${category}` : '';
    const response = await apiClient.get<{ success: boolean; data: PlatformSetting[] }>(
      `/admin/settings${params}`
    );
    return response.data.data ?? [];
  },

  async getSetting(key: string): Promise<PlatformSetting | null> {
    try {
      const response = await apiClient.get<{ success: boolean; data: { key: string; value: string } }>(
        `/admin/settings/${key}`
      );
      return response.data.data as PlatformSetting;
    } catch {
      return null;
    }
  },

  async updateSetting(key: string, value: string, meta?: { category?: string; description?: string; dataType?: string }): Promise<PlatformSetting> {
    const response = await apiClient.put<{ success: boolean; data: PlatformSetting }>(
      `/admin/settings/${key}`,
      { value, ...meta }
    );
    return response.data.data;
  },
};

export interface AdminServiceZone {
  id: string;
  name: string;
  city: string;
  district: string;
  isActive: boolean;
  priority: number;
  estimatedDeliveryMinutes: number | null;
  minimumOrderAmount: number | null;
  deliveryFee: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminServiceZoneWithGeoJson extends AdminServiceZone {
  boundaryGeoJson?: { type: string; coordinates: number[][][] };
}

export interface CreateServiceZoneDto {
  name: string;
  city: string;
  district: string;
  polygonCoordinates: [number, number][];
  isActive?: boolean;
  priority?: number;
  estimatedDeliveryMinutes?: number;
  minimumOrderAmount?: number;
  deliveryFee?: number;
}

export interface UpdateServiceZoneDto extends Partial<CreateServiceZoneDto> {}

export interface PlatformSetting {
  id?: string;
  key: string;
  value: string;
  category: string | null;
  description: string | null;
  dataType: string;
  updatedAt?: string;
}

export interface AdminRestaurantCategory {
  id: string;
  name: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  restaurantId: string;
}

export interface AdminCreateProductDto {
  name: string;
  description?: string;
  price: number;
  imageUrl?: string;
  displayOrder?: number;
  tags?: string;
  calories?: number;
  preparationMinutes?: number;
}
